import * as React from 'react';
import useGoogleAnalytics from 'analytics/hooks/useGoogleAnalytics';
import { loadSessionStorage } from 'lib/storage';
import { useABTesting } from 'core/components/ABTestingProvider/ABTestingProvider';
import { useSelector } from 'react-redux';
import type { BookingStepEvent } from '../lib/types';
import { getIsDigitalWalkIn } from '../redux/selectors';

type TrackBookingStep = (step: BookingStepEvent) => void;

export default function useTrackBookingStep(): TrackBookingStep {
    const { trackWithAnyGoogleAnalytics } = useGoogleAnalytics();
    const { getAllPageVariations } = useABTesting();
    const isDigitalWalkIn = useSelector(getIsDigitalWalkIn);

    // Memoize the page variations to avoid unnecessary recalculations
    const pageVariations = React.useMemo(
        () => getAllPageVariations(),
        [getAllPageVariations],
    );

    return React.useCallback(
        (step: BookingStepEvent) => {
            let event = `BF_${step}`;

            // Append variation to the event name if a relevant variation is active
            // hardcoded values to avoid crowding GA4
            if (pageVariations?.length) {
                if (
                    pageVariations.some((variation) =>
                        ['convertABTestPlaceholder'].includes(variation),
                    )
                ) {
                    if (step === 'date') {
                        event += `_${pageVariations.join('_')}`;
                    }
                }
            }

            if (
                isDigitalWalkIn &&
                ['client', 'pet', 'summary', 'finished'].includes(step)
            ) {
                event = `BF_${step}_digitalWalkin`;
            }

            const args: Record<string, string> = {
                event_category: 'appointment',
            };

            const origin = loadSessionStorage<string>('booking_origin');

            if (origin) {
                args.origin = origin;
            }

            trackWithAnyGoogleAnalytics(false, 'event', event, args);
        },
        [trackWithAnyGoogleAnalytics, pageVariations, isDigitalWalkIn],
    );
}
